import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import styles from '../../pages/Orders/Orders.module.css';
import { reverseGeocode, forwardGeocode } from '../../utils/nominatim';

// Corrige ícones padrão do Leaflet que quebram com webpack
import 'leaflet/dist/leaflet.css';

// Workaround para ícones do Leaflet no webpack
const iconUrl = 'https://unpkg.com/leaflet@1.9.4/dist/images/';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: `${iconUrl}marker-icon-2x.png`,
  iconUrl: `${iconUrl}marker-icon.png`,
  shadowUrl: `${iconUrl}marker-shadow.png`,
});

// Ícone personalizado azul para o marcador (fica mais visível no mapa)
const markerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

type LocationPickerProps = {
  latitude?: number | null;
  longitude?: number | null;
  searchAddress?: string;
  onChange: (coords: {
    latitude: number | null;
    longitude: number | null;
    address?: string;
    neighborhood?: string;
  }) => void;
};

const DEFAULT_CENTER: [number, number] = [-8.3592, -36.6964];
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// ─── Subcomponentes ────────────────────────────────────────────────

interface MapClickHandlerProps {
  onMapClick: (lat: number, lng: number) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

interface MapControllerProps {
  center: [number, number] | null;
  zoom: number;
}

const MapController: React.FC<MapControllerProps> = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom, { animate: true });
    }
  }, [map, center, zoom]);
  return null;
};

interface DraggableMarkerProps {
  position: [number, number];
  onDragEnd: (lat: number, lng: number) => void;
}

const DraggableMarker: React.FC<DraggableMarkerProps> = ({ position, onDragEnd }) => {
  const eventHandlers = useMemo(
    () => ({
      dragend: (e: L.LeafletEvent) => {
        const marker = e.target as L.Marker;
        const latlng = marker.getLatLng();
        onDragEnd(latlng.lat, latlng.lng);
      },
    }),
    [onDragEnd],
  );

  return (
    <Marker
      position={position}
      draggable={true}
      icon={markerIcon}
      eventHandlers={eventHandlers}
    >
      <Popup>Arraste para ajustar a localização da ocorrência</Popup>
    </Marker>
  );
};

// ─── Componente Principal ──────────────────────────────────────────

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  searchAddress,
  onChange,
}) => {
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSearchError, setAddressSearchError] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);

  const markerPosition: [number, number] | null =
    latitude != null && longitude != null ? [latitude, longitude] : null;

  const center: [number, number] = markerPosition || DEFAULT_CENTER;
  const zoom = markerPosition ? 18 : 14;

  // Notificar coordenadas + tentar reverse geocode
  const updateCoordinates = useCallback(
    async (lat: number, lng: number) => {
      const latitudeRounded = Number(lat.toFixed(6));
      const longitudeRounded = Number(lng.toFixed(6));

      onChange({
        latitude: latitudeRounded,
        longitude: longitudeRounded,
      });

      // Reverse geocode via Nominatim
      try {
        const result = await reverseGeocode(latitudeRounded, longitudeRounded);

        if (result.address) {
          setAddressQuery(result.address);
        }

        onChange({
          latitude: latitudeRounded,
          longitude: longitudeRounded,
          address: result.address || undefined,
          neighborhood: result.neighborhood || undefined,
        });
      } catch {
        // Mantém coordenadas mesmo se reverse geocode falhar
      }
    },
    [onChange],
  );

  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      setAddressSearchError('');
      void updateCoordinates(lat, lng);
    },
    [updateCoordinates],
  );

  const handleDragEnd = useCallback(
    (lat: number, lng: number) => {
      void updateCoordinates(lat, lng);
    },
    [updateCoordinates],
  );

  // Busca de endereço via Nominatim (forward geocode)
  const handleAddressSearch = useCallback(async () => {
    const query = addressQuery.trim();

    if (!query) {
      setAddressSearchError('Digite o nome da rua para localizar no mapa.');
      return;
    }

    setSearchingAddress(true);
    setAddressSearchError('');

    try {
      const results = await forwardGeocode(query);

      if (!results.length) {
        setAddressSearchError(
          'Endereço não encontrado. Tente detalhar com bairro/cidade.',
        );
        return;
      }

      const { latitude: lat, longitude: lng } = results[0];
      void updateCoordinates(lat, lng);
    } catch {
      setAddressSearchError('Não foi possível localizar o endereço informado.');
    } finally {
      setSearchingAddress(false);
    }
  }, [addressQuery, updateCoordinates]);

  // Preencher campo de busca com searchAddress da prop (se vier)
  useEffect(() => {
    if (!searchAddress?.trim()) {
      return;
    }
    setAddressQuery((currentValue) =>
      currentValue.trim() ? currentValue : searchAddress.trim(),
    );
  }, [searchAddress]);

  return (
    <div className={styles.locationPicker}>
      <p className={styles.locationHint}>
        Clique no mapa para marcar o ponto da ocorrência. Se necessário, arraste o
        marcador para ajustar.
      </p>

      <div className={styles.locationSearchRow}>
        <input
          className={styles.formInput}
          type="text"
          placeholder="Digite o nome da rua para localizar no mapa"
          value={addressQuery}
          onChange={(event) => {
            setAddressQuery(event.target.value);
            if (addressSearchError) {
              setAddressSearchError('');
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAddressSearch();
            }
          }}
        />
        <button
          type="button"
          className={styles.locationSearchButton}
          onClick={handleAddressSearch}
          disabled={searchingAddress}
        >
          {searchingAddress ? 'Localizando...' : 'Localizar rua'}
        </button>
      </div>

      {addressSearchError && (
        <div className={styles.locationSearchError}>{addressSearchError}</div>
      )}

      <div className={styles.locationMap}>
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ width: '100%', height: '320px' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />
          <MapClickHandler onMapClick={handleMapClick} />
          {markerPosition && (
            <>
              <DraggableMarker
                position={markerPosition}
                onDragEnd={handleDragEnd}
              />
              <MapController
                center={markerPosition}
                zoom={18}
              />
            </>
          )}
        </MapContainer>
      </div>

      <div className={styles.locationCoordinates}>
        <div className={styles.formGroup}>
          <label>Latitude</label>
          <input
            className={styles.formInput}
            type="number"
            step="any"
            value={latitude ?? ''}
            onChange={(event) =>
              onChange({
                latitude:
                  event.target.value === ''
                    ? null
                    : Number(event.target.value),
                longitude: longitude ?? null,
              })
            }
          />
        </div>
        <div className={styles.formGroup}>
          <label>Longitude</label>
          <input
            className={styles.formInput}
            type="number"
            step="any"
            value={longitude ?? ''}
            onChange={(event) =>
              onChange({
                latitude: latitude ?? null,
                longitude:
                  event.target.value === ''
                    ? null
                    : Number(event.target.value),
              })
            }
          />
        </div>
      </div>

      <div className={styles.locationActions}>
        <button
          type="button"
          className={styles.locationClearButton}
          onClick={() => onChange({ latitude: null, longitude: null })}
        >
          Limpar marcação
        </button>
      </div>
    </div>
  );
};

export default LocationPicker;